import { getAgentCursor } from "./agent-cursor";
import type { DomActionCallPayload, DomActionResultPayload } from "./protocol";

/**
 * Sets a form element's value the way a real user typing would — plain
 * `el.value = x` does NOT notify frameworks with controlled inputs (React
 * chief among them): they override the native value setter to track state,
 * so a direct assignment is invisible to them and the UI silently doesn't
 * update. Calling the *native* prototype's setter first, then dispatching a
 * real `input` event, is the standard workaround.
 */
function setNativeValue(
  el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  value: string,
): void {
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : el instanceof HTMLSelectElement
        ? HTMLSelectElement.prototype
        : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) {
    setter.call(el, value);
  } else {
    el.value = value;
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * `el.click()` alone never fires `pointerdown`/`mousedown`/`pointerup`/
 * `mouseup` — only a `click` event. Plain `<button onClick>` handlers don't
 * care, but Radix UI's interactive primitives (Tabs, Select, Menu, and
 * others) react on `pointerdown` for a snappier feel, so a `.click()`-only
 * "click" silently did nothing on them — confirmed live: dispatching just a
 * synthetic click left a Radix tab on its previous selection, while adding
 * the pointer/mouse pair first switches it.
 *
 * The final step stays a real `el.click()` (not a dispatched synthetic
 * MouseEvent) deliberately: a script-dispatched click is "untrusted" and
 * skips the browser's native activation behavior (checkbox toggling, a
 * plain `<a href>` actually navigating, form submission), which `.click()`
 * still triggers. Only the leading pointer/mouse events are synthetic.
 */
function simulateRealClick(el: HTMLElement): void {
  const rect = el.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  const base = { bubbles: true, cancelable: true, view: window, clientX: x, clientY: y, button: 0 };
  el.dispatchEvent(new PointerEvent("pointerdown", base));
  el.dispatchEvent(new MouseEvent("mousedown", base));
  el.dispatchEvent(new PointerEvent("pointerup", base));
  el.dispatchEvent(new MouseEvent("mouseup", base));
  el.click();
}

/**
 * Executes one mapped action against the real page's DOM. Runs entirely
 * inside the visitor's browser — this is the only place in the whole
 * pipeline where a dom_action tool actually touches the customer's product.
 *
 * Security note: `navigate` only ever follows a real `<a>` element's own
 * `href`, and only if it resolves same-origin. It never navigates to a
 * value supplied by the agent — that would let a prompt-injected or
 * otherwise misbehaving call redirect the visitor off-site.
 */
// Actions a visitor would actually see happen at a specific spot on the
// page — the cursor visits these before they run. `read_text` doesn't (it's
// a silent read, nothing for a real user to "do" at that element).
const VISIBLE_ACTIONS = new Set<DomActionCallPayload["action"]>([
  "click",
  "fill",
  "select",
  "navigate",
]);

export async function executeDomAction(
  payload: DomActionCallPayload,
): Promise<DomActionResultPayload> {
  const base: Pick<DomActionResultPayload, "call_id"> = {
    call_id: payload.call_id,
  };
  let el: Element | null;
  try {
    el = document.querySelector(payload.selector);
  } catch (err) {
    return {
      ...base,
      success: false,
      error: `Invalid selector: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  if (!el) {
    return { ...base, success: false, error: "Element not found" };
  }

  if (VISIBLE_ACTIONS.has(payload.action) && el instanceof HTMLElement) {
    // A mapped element can be below the fold (a long dropdown list, a
    // scrolled-down page) — scroll it into view before measuring its
    // position, or the cursor lands on stale coordinates and the click
    // that follows misses entirely. One frame lets the scroll actually
    // settle before we read getBoundingClientRect() below.
    el.scrollIntoView({ block: "nearest", inline: "nearest" });
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  const cursor = VISIBLE_ACTIONS.has(payload.action) ? getAgentCursor() : null;
  if (cursor) {
    await cursor.moveTo(el);
  }

  try {
    switch (payload.action) {
      case "click": {
        if (!(el instanceof HTMLElement)) {
          return { ...base, success: false, error: "Element is not clickable" };
        }
        await cursor?.pulse();
        simulateRealClick(el);
        return { ...base, success: true };
      }
      case "fill": {
        if (
          !(el instanceof HTMLInputElement) &&
          !(el instanceof HTMLTextAreaElement)
        ) {
          return { ...base, success: false, error: "Element is not a text input" };
        }
        await cursor?.pulse();
        setNativeValue(el, payload.value ?? "");
        return { ...base, success: true };
      }
      case "select": {
        if (!(el instanceof HTMLSelectElement)) {
          return { ...base, success: false, error: "Element is not a select" };
        }
        await cursor?.pulse();
        setNativeValue(el, payload.value ?? "");
        return { ...base, success: true };
      }
      case "read_text": {
        const text = (el instanceof HTMLElement ? el.innerText : el.textContent) || "";
        return { ...base, success: true, value: text.trim() };
      }
      case "navigate": {
        if (!(el instanceof HTMLAnchorElement)) {
          return { ...base, success: false, error: "Element is not a link" };
        }
        let url: URL;
        try {
          url = new URL(el.href, window.location.href);
        } catch {
          return { ...base, success: false, error: "Invalid link" };
        }
        if (url.origin !== window.location.origin) {
          return { ...base, success: false, error: "Cross-origin navigation blocked" };
        }
        await cursor?.pulse();
        window.location.href = url.href;
        return { ...base, success: true };
      }
      default:
        return { ...base, success: false, error: `Unknown action: ${payload.action}` };
    }
  } catch (err) {
    return {
      ...base,
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
